import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Star, Trash2, Upload, Loader2, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { ProductFotoGrid } from "@/components/ProductFotoGrid";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MAX = 10;

export const ProductFotos = ({ productId }) => {
  const [fotos, setFotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/producten/${productId}/afbeeldingen`);
      setFotos(data || []);
    } catch { /* leeg */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [productId]);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (fotos.length + files.length > MAX) {
      return toast.error(`Maximaal ${MAX} foto's per product`);
    }
    setBusy(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        await api.post(`/producten/${productId}/afbeeldingen`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Upload van ${file.name} mislukt`);
      }
    }
    setBusy(false);
    await load();
    toast.success("Foto's geüpload");
  };

  const verwijder = async (imgId) => {
    await api.delete(`/producten/${productId}/afbeeldingen/${imgId}`);
    await load();
  };

  const maakHoofdfoto = async (imgId) => {
    const volgorde = [imgId, ...fotos.filter((f) => f.id !== imgId).map((f) => f.id)];
    await api.put(`/producten/${productId}/afbeeldingen/volgorde`, { volgorde });
    await load();
    toast.success("Hoofdfoto ingesteld");
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="product-fotos">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-medium">Productfoto's</div>
          <div className="text-xs text-muted-foreground">
            Max {MAX} foto's. De hoofdfoto (★) staat vooraan op de webshop-productpagina. {fotos.length}/{MAX} gebruikt.
          </div>
        </div>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif"
          multiple hidden onChange={onPick} data-testid="foto-input" />
        <Button onClick={() => fileRef.current?.click()} disabled={busy || fotos.length >= MAX}
          data-testid="foto-upload-btn">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
          Foto's toevoegen
        </Button>
      </div>

      {fotos.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground border border-dashed border-border rounded-lg">
          <ImageOff className="h-8 w-8 mb-2" />
          <div className="text-sm">Nog geen foto's. Voeg er maximaal {MAX} toe voor de webshop.</div>
        </div>
      ) : (
        <>
          {fotos.length >= 1 && (
            <div className="border border-border rounded-lg p-4 bg-secondary/20">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Voorbeeld op presentatie</div>
              <ProductFotoGrid
                fotos={fotos.slice(0, 3).map((f) => ({ url: `${BACKEND}${f.url}`, alt: f.original_filename || "productfoto" }))}
                alt="Product"
              />
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {fotos.map((f) => (
              <div key={f.id} className="relative group rounded-lg overflow-hidden border border-border aspect-square bg-secondary/30"
                data-testid={`foto-${f.id}`}>
                <img src={`${BACKEND}${f.url}`} alt={f.original_filename || "productfoto"}
                  className="w-full h-full object-cover" loading="lazy" />
                {f.hoofdfoto && (
                  <span className="absolute top-1.5 left-1.5 bg-accent text-accent-foreground text-[10px] font-medium px-1.5 py-0.5 rounded-full flex items-center gap-1">
                    <Star className="h-3 w-3 fill-current" /> Hoofdfoto
                  </span>
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  {!f.hoofdfoto && (
                    <button onClick={() => maakHoofdfoto(f.id)} title="Als hoofdfoto"
                      className="bg-white/90 text-foreground rounded-full p-2 hover:bg-white"
                      data-testid={`foto-hoofd-${f.id}`}><Star className="h-4 w-4" /></button>
                  )}
                  <button onClick={() => verwijder(f.id)} title="Verwijderen"
                    className="bg-white/90 text-destructive rounded-full p-2 hover:bg-white"
                    data-testid={`foto-verwijder-${f.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
